import React, { FC, useCallback, useState } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { useLocation, useHistory, Redirect } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { Carousel, FeedbackCard, FeedbackCardProps, LoginFormData } from "@xapp/components";

import { setToken, setProfile } from "auth";
import { Credentials } from "auth/modules/state";
import { UserProfile } from "generated/graphql";
import { State } from "redux/state";

import { FetchProfileComponent } from "components/Authorize/components/FetchProfileComponent";
import { SignUp, SignUpProps, SignUpSession } from "components/Login/components/SignUp";

import style from "./style.scss";

function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

function useAddQueryParams() {
    const history = useHistory();

    type QueryParamsType = { [key: string]: string };
    return useCallback(
        (newParams: QueryParamsType) => {
            const searchParams = new URLSearchParams(history.location.search);
            for (const key in newParams) {
                searchParams.set(key, newParams[key]);
            }
            history.replace({ ...history.location, search: searchParams.toString() });
        },
        [history]
    );
}

export interface OnboardingSignUpContainerProps extends SignUpProps {}

/**
 * Only used during onboarding, sign up
 */
export const OnboardingSignUpContainer: FC<OnboardingSignUpContainerProps> = (props) => {
    const location = useLocation();

    const setQueryParams = useAddQueryParams();
    const queryParams = useQueryParams();

    const dispatch = useDispatch();
    const credentials = useSelector<State, Credentials>((state) => state.auth.credentials);
    const profile = useSelector<State, UserProfile>((state) => state.auth.profile); // useProfile();

    const displayCodeDialog = queryParams.get("displayCode") === "true";
    const email = queryParams.get("email");
    const website = queryParams.get("website");

    const [formData, setFormData] = useState<LoginFormData>();

    const handleSignUpSuccess = useCallback(
        (data: LoginFormData) => {
            // they signed up, set #verify
            // keep existing
            const queryParams: { [key: string]: string } = {
                displayCode: "true",
                email: data.email,
            };

            if (data.website) {
                queryParams.website = data.website;
            }

            setQueryParams(queryParams);
        },
        [setQueryParams]
    );

    const handleSignUpWithCodeVerified = useCallback(
        (data: LoginFormData) => {
            const queryParams: { [key: string]: string } = {
                email: data.email,
            };
            if (data.website) {
                queryParams.website = data.website;
            }

            setQueryParams(queryParams);
        },
        [setQueryParams]
    );

    const handleSignIn = useCallback(
        (session: SignUpSession, data: LoginFormData) => {
            setFormData(data);
            dispatch(setToken(session));
        },
        [dispatch]
    );

    const handleProfile = useCallback(
        (profile: UserProfile) => {
            dispatch(setProfile(profile));
        },
        [dispatch]
    );

    // if we have credentials, we logged in successfully
    // see the logic over in AuthorizeComponent
    // this is very similar to the if (token) { <FetchProfile}
    if (credentials && !profile) {
        return (
            <div className={style.stepWrapper}>
                <FetchProfileComponent
                    accessToken={credentials.accessToken}
                    onError={(e) => {
                        console.log(`error fetching profile ${e}`);
                    }}
                    onProfile={handleProfile}
                />
            </div>
        );
    }

    if (profile) {
        const search = new URLSearchParams(location.search);

        // One possibility here is we are accepting an invite so we have to redirect them to /accept?inviteId={inviteId}
        const inviteId = search.get("inviteId");
        if (inviteId) {
            const redirectUrl = `/accept?inviteId=${inviteId}`;
            console.log("Redirecting accept invite to:", redirectUrl);
            return <Redirect to={redirectUrl} />;
        }

        if (formData?.email) {
            search.set("email", formData.email);
        }

        if (formData?.website) {
            search.set("website", formData.website);
        }

        const path = `/onboarding?${search.toString()}`;

        return <Redirect to={path} />;
    }

    const card1: FeedbackCardProps = {
        avatarSRC: "/img/quotes/all-pro-plumbing-logo.png",
        description:
            '"Our AI Assistant from XAPP AI increases our ability to capture leads and remain responsive to customers 24/7 and especially after hours when our CSRs our off-duty."',
        author: "All Pro Plumbing",
        rating: false,
    };

    /*
    const card2: FeedbackCardProps = {
        avatarSRC: "/img/quotes/data-moose-logo.png",
        description: '"Self On-boarding really was faster and easier than I thought. I absolutely love the customization that\'s available. This adds a touch of class to our site!"',
        author: "John Seaman, CEO & Co-Founder | Data Moose",
        rating: false
    }
    */

    const card3: FeedbackCardProps = {
        avatarSRC: "/img/quotes/john-c-flood-logo.png",
        description:
            "XAPP AI Scheduler and XAPP AI Chat solutions are better, cheaper and faster than the competition.  XAPP AI has improved our customer experience 24/7/365, and they’re delivering greater than 10x return on investment for our business.",
        author: "John C. Flood",
        rating: false,
    };

    function getInnerComponents(recaptchaKey?: string) {
        return (
            <>
                <div className={style.signUpWrapper}>
                    <div className={style.contentWrapper}>
                        <div className={style.asideWrapper}>
                            <div className={style.valueDriverWrapper}>
                                <img style={{ width: "250px" }} src="/img/XAPPman.png" alt="XAPPman" />
                                <div style={{ width: "300px" }}>
                                    <h2>AI Designed for Home Services to...</h2>
                                    <ul>
                                        <li>Answer Questions</li>
                                        <li>Schedule Jobs</li>
                                        <li>Provide Self-service</li>
                                        <li>Grow your business</li>
                                    </ul>
                                </div>
                            </div>

                            <Carousel interval={5000}>
                                <FeedbackCard {...card1} />
                                <FeedbackCard {...card3} />
                            </Carousel>
                        </div>
                        <div className={style.rightWrapper}>
                            {/*<div className={style.mobileBrandingWrapper}>
                            <a href="https://xapp.ai" target={"_blank"} rel="noreferrer">
                                <img src="/img/xapp-logo-teal.png" alt="XAPP AI Logo" />
                            </a>
                            <a href="https://xapp.ai" target={"_blank"} rel="noreferrer">
                                <p>XAPP AI</p>
                            </a>
                          </div>
                           */}
                            <SignUp
                                {...props}
                                className={style.formWrapper}
                                data={{ email, website }}
                                displaySocialLogins={true}
                                title={"Let's Get Started!"}
                                subtitle={"Create an account to build your custom AI"}
                                showVerificationCodeDialog={displayCodeDialog}
                                onSignUpSuccess={handleSignUpSuccess}
                                onCodeVerified={handleSignUpWithCodeVerified}
                                onSignIn={handleSignIn}
                                recaptchaSiteKey={recaptchaKey}
                                passwordRequirements={{
                                    minLength: 8,
                                    maxLength: 20,
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className={style.logoWrapper}>
                    <a href="https://xapp.ai" target={"_blank"} rel="noreferrer">
                        <img src="/img/xapp-ai-horizontal-2color-rgb.png" alt="XAPP AI Logo" />
                    </a>
                </div>
            </>
        );
    }

    const reCaptchaKey = window?.xaStudioConfig?.reCaptchaSiteKey;

    if (reCaptchaKey) {
        // Only applying GoogleReCaptchaProvider to the Onboarding Signup container
        // as this is where we have seen the most bots in the past
        // We can reconsider this in the future.
        return (
            <GoogleReCaptchaProvider reCaptchaKey={reCaptchaKey}>
                {getInnerComponents(reCaptchaKey)}
            </GoogleReCaptchaProvider>
        );
    } else {
        return getInnerComponents();
    }
};
